/**
 * pages/signup/SignupShell.tsx — layout wrapper for every signup step.
 *
 * Every /signup/* page renders inside this shell so:
 *   1. The header is consistent (brand, back button when applicable,
 *      progress dots).
 *   2. Errors get a common banner surface.
 *   3. The shell can force-clear signup progress on mount for the
 *      terminal `blocked` state — leaving persisted progress in that
 *      case would try to route the user back into the same flow on
 *      the next visit.
 *
 * This shell is INTENTIONALLY minimal — a visual polish pass will come
 * later via the design agent. The goal here is: real screens, real
 * data flow, no fake mocks.
 */

import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "../../components";
import styles from "./SignupShell.module.css";

export interface SignupShellProps {
  title: string;
  subtitle?: string;
  /** Step number (1..N). Renders progress dots. Omit for terminal
   * states (blocked / rejected / verifying) where progress doesn't
   * make sense. */
  step?: number;
  /** Total steps, defaults to 5 (legal → name → dob → address → ssn). */
  totalSteps?: number;
  /** Where the browser-back / arrow lands. Omit for step 1 and
   * terminal states. */
  backTo?: string;
  children: ReactNode;
  /** Error banner content — set from the last failed API call. */
  errorBanner?: string | null;
}

export function SignupShell({
  title,
  subtitle,
  step,
  totalSteps = 5,
  backTo,
  children,
  errorBanner,
}: SignupShellProps) {
  return (
    <main className={styles.root}>
      <div className={styles.themeToggle}>
        <ThemeToggle />
      </div>

      <div className={styles.shell}>
        <header className={styles.header}>
          {backTo ? (
            <Link to={backTo} className={styles.back} aria-label="Go back">
              ← Back
            </Link>
          ) : (
            <span className={styles.backPlaceholder} />
          )}
          {step !== undefined && (
            <div
              className={styles.dots}
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={totalSteps}
              aria-valuenow={step}
              aria-label={`Step ${step} of ${totalSteps}`}
            >
              {Array.from({ length: totalSteps }, (_, i) => (
                <span
                  key={i}
                  className={i < step ? styles.dotFilled : styles.dotEmpty}
                />
              ))}
            </div>
          )}
        </header>

        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

        {errorBanner && (
          <div role="alert" className={styles.errorBanner}>
            {errorBanner}
          </div>
        )}

        <div className={styles.body}>{children}</div>
      </div>
    </main>
  );
}
